<template>
  <VTMultiselect
    v-if="triggerOptions"
    :items="triggerOptions"
    :enable-search="true"
    :selected-items="selectedTriggers"
    @toggle-selection="handleTriggerUpdate"
    @add-item="handleAddTrigger"
    @delete-item="handleDeleteTrigger"
  ></VTMultiselect>
</template>

<script setup>
import VTMultiselect from "@/components/VTMultiSelect";
import useToast from "@/hooks/useToastNotification";
import { useTriggers } from "@/hooks/useTriggers";
import containsOnlyAlphabetical from "@/utils/isOnlyAlphabetical";
import isPascalCase from "@/utils/isPascalCase";
import { useConfirm } from "primevue/useconfirm";
import { computed } from "vue";

const props = defineProps({
  selectedTriggers: {
    type: Array,
    default: () => [],
  },
  gameId: {
    type: String,
    required: true,
  },
});

const emit = defineEmits(["addTrigger", "updateTrigger", "deleteTrigger"]);

const { triggers, addTrigger, removeTrigger } = useTriggers(props.gameId);
const confirm = useConfirm();
const { toastError } = useToast();

const triggerOptions = computed(() =>
  triggers.value?.map((trigger) => ({
    name: trigger.name,
    value: trigger.id,
    isDeletable: trigger.name !== "MainMenu",
  }))
);

const handleTriggerUpdate = (trigger) => {
  emit("updateTrigger", trigger);
};

async function handleAddTrigger(trigger) {
  const triggerIsPascalCase = isPascalCase(trigger);
  const triggerContainsOnlyAlphabetical = containsOnlyAlphabetical(trigger);

  if (!triggerContainsOnlyAlphabetical) {
    toastError({
      detail: "A trigger must contain only alphabetical characters.",
      summary: "Error",
    });
    return;
  }

  if (!triggerIsPascalCase) {
    toastError({
      detail: "A trigger must be in PascalCase.",
      summary: "Error",
    });
    return;
  }

  if (triggerContainsOnlyAlphabetical && triggerIsPascalCase) {
    const addedTrigger = await addTrigger(trigger);
    emit("addTrigger", addedTrigger);
  }
}

const handleDeleteTrigger = (trigger) => {
  confirm.require({
    message: `Are you sure you want to delete the trigger "${trigger.name}"?`,
    header: "Delete Confirmation",
    acceptLabel: "Yes",
    rejectLabel: "No",
    acceptClass: "p-button-danger",
    accept: async () => {
      await removeTrigger(trigger.value);
      handleDeleteEmit();
    },
    reject: () => {
      console.error("Delete operation cancelled.");
    },
  });
};

function handleDeleteEmit() {
  emit("deleteTrigger");
}
</script>

<style module></style>
